"use client";
import { useMarket } from "@app/_context";
import useTranslate from "@app/_hooks/useTranslate";
import { useCallback, useEffect } from "react";

import {
  isAdyenCheckoutEnabled,
  isWalleyCheckoutEnabled,
} from "@/config/checkout";
import { cn } from "@/lib/utils";
import { getCartCookie } from "@/utils/cart/getCartCookie";

import {
  useCheckout,
  useCheckoutContext,
  useCheckoutZipCode,
} from "../Checkout.hooks";
import { checkoutStore } from "../Checkout.store";
import CheckoutMessage from "../CheckoutMessage";
import {
  useCheckoutDeliveries,
  useCheckoutDelivery,
  useSetCheckoutDelivery,
} from "./Delivery.hooks";
import { type DeliveryOption as Option } from "./Delivery.utils";
import DeliveryOption from "./DeliveryOption";
import DeliverySkeleton from "./DeliverySkeleton";

const DELIVERY_ITEM_PARTNO = "1000000";

export default function CheckoutDelivery() {
  const t = useTranslate();
  const marketConfig = useMarket().state.market;
  const { data: checkout } = useCheckout();
  const { deliveryStepRef, paymentStepRef } = useCheckoutContext();
  const zipCode = useCheckoutZipCode();
  const { mutateAsync } = useSetCheckoutDelivery();
  const { data: delivery, ...checkoutDelivery } = useCheckoutDelivery();
  const { data: deliveryImages } = useCheckoutDeliveries();
  const setDeliveryOption = checkoutStore((state) => state.setDeliveryOption);
  const setCanScrollToDelivery = checkoutStore(
    (state) => state.setCanScrollToDelivery,
  );

  const isPending = checkoutDelivery.isPending;

  const onChange = useCallback(
    async (option: Option) => {
      const cartCookie = getCartCookie();

      if (isAdyenCheckoutEnabled({ marketConfig })) {
        // do stuff
      } else if (isWalleyCheckoutEnabled({ marketConfig })) {
        window.walley?.checkout?.api?.suspend?.();
      } else {
        // do stuff
      }
      return mutateAsync({
        locale: marketConfig.serviceLocale,
        zipCode: zipCode ?? "",
        cartId: cartCookie?.value ?? "",
        deliveryOptionName: option.name,
        deliveryOptionId: option.id,
        deliveryAgentId: option.agent,
        priceValue: option.price,
      }).then(() => setDeliveryOption(option));
    },
    [marketConfig, mutateAsync, setDeliveryOption, zipCode],
  );

  useEffect(() => {
    if (!delivery) return;
    if (!zipCode) return;
    if (!checkout?.items.length) return;

    const deliveryItem = checkout?.items.find(
      (item) => item.partNo === DELIVERY_ITEM_PARTNO,
    );

    if (!deliveryItem) {
      onChange(delivery.options[0]);
      return;
    }

    const deliveryItemId = deliveryItem.extras?.DELIVERY_OPTION_ID;

    if (!deliveryItemId) {
      onChange(delivery.options[0]);
      return;
    }

    if (!delivery?.options.some((opt) => opt.id === deliveryItemId)) {
      onChange(delivery.options[0]);
      // Why return here? This is the last thing that happens in the useEffect 🤔
      return;
    }
    const deliveryOption = delivery.options.find(
      (opt) => opt.id === deliveryItemId,
    );
    if (!deliveryOption) {
      return;
    }
    if (deliveryOption.price !== checkout.totalFreight) {
      onChange(deliveryOption);
    }
  }, [checkout, delivery, zipCode, onChange]);

  useEffect(() => {
    if (!deliveryStepRef?.current) return;

    const config = { attributes: true, childList: true, subtree: true };

    const callback = (mutationList: MutationRecord[]) => {
      for (const mutation of mutationList) {
        if (mutation.type === "childList") {
          setCanScrollToDelivery(true);
        }
      }
    };

    const observer = new MutationObserver(callback);

    observer.observe(deliveryStepRef.current, config);

    return () => {
      observer.disconnect();
    };
  }, [deliveryStepRef, setCanScrollToDelivery]);

  if (!zipCode) return null;

  if (isPending) {
    return <DeliverySkeleton />;
  }

  if (checkoutDelivery.isError) {
    return (
      <CheckoutMessage type="error">
        {t("deliveryFetchError") ?? "Failed to fetch delivery options"}
      </CheckoutMessage>
    );
  }

  return (
    <form
      ref={deliveryStepRef}
      className={cn(
        "scroll-mt-[7.5rem] lg:scroll-mt-[9rem]",
        isPending && "blur-sm",
        "divide-y border transition-all",
      )}
    >
      {delivery?.options?.map((option) => (
        <DeliveryOption
          key={option.id}
          option={option}
          image={deliveryImages?.get(option.id)}
          disabled={isPending}
          onSelect={() => {
            // If there are agents, don't scroll to the payment step
            // This is to make sure the user can see the agents and choose one if needed
            if (option.agents?.length && option.agents.length > 1) {
              return;
            }

            paymentStepRef?.current?.scrollIntoView({
              block: "start",
              behavior: "smooth",
            });
          }}
        />
      ))}
      {!delivery?.options.length && (
        <CheckoutMessage type="error">
          {t("deliveryNotFoundError", { zipCode }) ??
            `No delivery options available for zip code ${zipCode}`}
        </CheckoutMessage>
      )}
    </form>
  );
}
